import React, { useCallback, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import PlacementUpdateMenu from 'src/components/shared/placementUpdateMenu';
import { TRANSFER_PENDING } from 'src/config/AccommodationStatusNames';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import * as LDAP from 'src/config/LDAP';
import JmrService from 'src/services/jmr/jmrService';
import { setJmrFlowType } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import {
  setIsConfirmDeleteModalOpen,
  setIsEmailModalOpen,
  setIsOomdModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';
import { JmrFlowTypes } from 'src/utils/jmrUtils';

interface TopOptionsProps {
  openTwpaModal: VoidFunction;
}

/**
 * Default component
 */
const TopOptions = ({ openTwpaModal }: TopOptionsProps) => {
  const { placementId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exportButtonRef = useRef();
  const updateButtonRef = useRef<HTMLButtonElement>(null);
  const { ldap } = useAppSelector((state) => state.user);
  const { consolidatedRegionTasks } = useAppSelector((state) => state.newJmrPage);
  const { placementDetails, accommodationStatus: accommodationStatusesForSite } = useAppSelector(
    (state) => state.placementDetailsPage,
  );
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isUpdateMenuOpen, setIsUpdateMenuOpen] = useState(false);
  const isAdmin = ldap.includes(LDAP.ADMIN);

  const downloadJmr = useCallback(async () => {
    const { data } = await JmrService.downloadJMR(placementDetails.newestJmrFileName);
    if (data) {
      downloadFile(createFileUrl(data, 'application/pdf'), 'jmr-report.pdf');
    }
  }, [placementDetails?.newestJmrFileName]);

  return (
    <Row maxWidth="100%" widths={['', 'fill']}>
      {/* Associate/Placement Info */}
      <Text type="d100" data-cy="placement-details__name-header">
        {placementDetails.associateFullName || placementDetails.login || ''}
      </Text>
      <div>
        <Tag type="warning" data-cy="placement-details__status-tag">
          {placementDetails?.accommodationStatus?.status}
        </Tag>
      </div>

      {/* Delete Placement */}
      {isAdmin && (
        <Button
          data-cy="placement-details__delete-btn"
          type="link"
          onClick={() => dispatch(setIsConfirmDeleteModalOpen(true))}
        >
          Delete Placement
        </Button>
      )}

      {/* Send Email/Update/Export */}
      {placementDetails?.accommodationStatus?.status !== TRANSFER_PENDING && (
        <>
          <Button
            type="secondary"
            onClick={() => dispatch(setIsEmailModalOpen(true))}
            data-cy="placement-detail-send-email__btn"
          >
            Send Email
          </Button>
          <Button
            id="update-placement-open-menu-btn"
            data-cy="placement-detail__update-btn"
            type="primary"
            ref={updateButtonRef}
            onClick={() => setIsUpdateMenuOpen(true)}
          >
            Update
            <Icon tokens={chevronDownSmallTokens} />
          </Button>
          <Button type="primary" ref={exportButtonRef} onClick={() => setIsExportMenuOpen(true)}>
            Export
            <Icon tokens={chevronDownSmallTokens} />
          </Button>
        </>
      )}

      {/* Accept Transfer */}
      {placementDetails?.accommodationStatus?.status === TRANSFER_PENDING && (
        <Button
          type="primary"
          data-cy="placement-details__accept-transfer-btn"
          onClick={() => {
            dispatch(setJmrFlowType(JmrFlowTypes.CompleteTransfer));
            navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.CompleteTransfer}`);
          }}
        >
          Accept Transfer
        </Button>
      )}

      <PlacementUpdateMenu
        isMenuOpen={isUpdateMenuOpen}
        closeMenu={() => setIsUpdateMenuOpen(false)}
        placementId={placementId!}
        anchorRef={updateButtonRef}
        accommodationStatusesForSite={accommodationStatusesForSite ?? []}
        managerAlias={placementDetails.managerAlias}
        fc={placementDetails.fc}
        accommodationType={placementDetails.accommodationType}
        placementStatus={placementDetails.accommodationStatus?.status}
        workRestrictions={placementDetails.workRestrictions}
        modifiedShiftLength={placementDetails.modifiedShiftLength}
        externalCaseId={placementDetails.externalCaseId}
        regionTasks={consolidatedRegionTasks}
      />

      {/* Export Menu */}
      <Menu
        anchorNode={exportButtonRef.current}
        open={isExportMenuOpen}
        position="bottom"
        onClose={() => setIsExportMenuOpen(false)}
      >
        {placementDetails.newestJmrFileName && <MenuItem onClick={downloadJmr}>Export JMR</MenuItem>}
        {placementDetails?.countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID ? (
          <MenuItem onClick={() => dispatch(setIsOomdModalOpen(true))}>OOMD PDF</MenuItem>
        ) : (
          <MenuItem onClick={openTwpaModal}>TWPA PDF</MenuItem>
        )}
      </Menu>
    </Row>
  );
};

export default TopOptions;
